/**
 * Landing v2 — Scene 2 "The Gap" market numbers.
 *
 * Source of truth: research/landing-v2/07-market-research.md §4 (final
 * recommended numbers, ex-Cursor framing) + §6 (fact-check risk register).
 *
 * Framing decision: we use the **ex-Cursor** alternative framing ($1B+ AI
 * founder tooling, Q1 2026) instead of the $3.3B all-in bold read. Rationale:
 * Cursor ($2B ARR) is a developer IDE, not a founder tool. Including it
 * overstates the competitive pressure on Foundry's ICP (non-developer
 * idea-stage founder). Ex-Cursor: Lovable + Replit + Bolt + base44 + Windsurf
 * + v0 ≈ $1.0B, plus landing tools (Webflow $213M 2024 + Framer $50M + Carrd
 * $2M) ≈ $250M. TAM label uses $1.0B (rounded to one decimal for readability
 * on a chart; exact is ~$990M–$1.05B).
 *
 * Fact-check hardening (07-market-research.md §6):
 *   - Lovable $400M — HIGH confidence (TechCrunch 2026-03-11 CEO disclosed)
 *   - Replit $250M — HIGH confidence (Mar 2026 funding round at $9B valuation)
 *   - base44 $100M — HIGH confidence (Ctech + Wix Q4 2025 filings)
 *   - Bolt.new $100M — MEDIUM (company forecast, not audited) — use tilde
 *   - Windsurf $82M — MEDIUM-HIGH (pre-Cognition acquisition, July 2025)
 *   - v0 $60M — MEDIUM (Sacra estimate, Vercel doesn't disclose) — use tilde
 *   - Webflow $213M (2024 calendar) — HIGH confidence; avoid unverified 2025
 *   - Framer $50M — HIGH (Aug 2025 Series D disclosed)
 *   - Carrd ~$2M — MEDIUM (founder AMA self-report)
 *   - Gap <$30M — "niche finder" category empty above noise line (07 §3)
 */

import type { MarketResearch } from "./types";

export const MARKET_RESEARCH: MarketResearch = {
  framing: "ex-Cursor",
  framingNote:
    "Excludes Cursor ($2B ARR) — an IDE for shipping developers, not Foundry's idea-stage ICP. Includes the AI app/code builders used by non-devs.",

  aiToolingTam: {
    value: "$1.0B",
    rawValue: 1_000_000_000,
    label: "AI app + landing builders combined ARR",
    date: "Q1 2026",
    sources: [
      "TechCrunch — Lovable $400M (2026-03-11)",
      "Replit funding disclosure (2026-03)",
      "Wix Q4 2025 earnings — base44 $100M",
      "Sacra profiles — Bolt, Windsurf, v0",
      "07-market-research.md §2–§4",
    ],
    confidence: "medium-high",
  },

  codeBuildersShave: {
    percent: 75,
    value: "$750M",
    rawValue: 750_000_000,
    label: "Code builders (Lovable, Replit, Bolt, base44, Windsurf, v0)",
    namedPlayers: [
      "Lovable $400M",
      "Replit $250M",
      "Bolt $100M",
      "base44 $100M",
      "Windsurf $82M",
      "v0 ~$60M",
    ],
  },

  landingBuildersShave: {
    percent: 25,
    value: "$250M",
    rawValue: 250_000_000,
    label: "Landing / layout tools (Webflow, Framer, Carrd)",
    namedPlayers: ["Webflow $213M (2024)", "Framer $50M", "Carrd ~$2M"],
  },

  uncovered: {
    percent: 2,
    // Raw 2% of a 600px bar ≈ 12px — visually invisible. Clamp to 12% so
    // the emerald sliver reads "small-but-real", not "absent". Label tells
    // the truth ("<2%"), visual tells the feeling.
    visualPercent: 12,
    value: "<$30M",
    rawValue: 30_000_000,
    label: "THE GAP — nobody finds you the niche",
    narrative: "Before code. Before landing. Before brand.",
  },

  whyNow: {
    line1:
      "You didn\u2019t lose three weekends because you\u2019re slow. You lost them because you skipped step zero.",
    line2:
      "2026 is the first year AI can run step zero for you \u2014 in thirty minutes.",
    line3:
      "The <10% who pick right are about to stop being rare.",
  },

  // sourcesNote retained for type compatibility but no longer surfaced by
  // Scene 2 — the Why Now coda dropped competitor-ARR citations when the
  // waterfall shifted from market-share positioning to founder-failure patterns.
  sourcesNote: "",
};
